import { useFramesStore, type FramePatchEntry } from "@/stores/frames-store";

interface RebindConversationFrameRuntimeArgs {
  conversationId?: string;
  executionId?: string;
  messageSequence?: number;
  sessionId?: string;
  spaceId?: string;
}

function metadataString(
  metadata: Record<string, unknown> | undefined,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = metadata?.[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return undefined;
}

/**
 * Rebind transient, loaded chat windows to a continuation's new runtime.
 *
 * Durable chat windows use Conversation ids rather than persisted canvas
 * Frame ids, so their live execution/runtime metadata must remain local UI
 * state. The canonical message event arrives before execution progress and is
 * therefore the earliest authoritative point at which cursor ownership can be
 * updated without a race.
 */
export function rebindLoadedConversationFrameRuntime({
  conversationId,
  executionId,
  messageSequence,
  sessionId,
  spaceId,
}: RebindConversationFrameRuntimeArgs): string[] {
  if (!conversationId || !executionId || !spaceId) return [];

  const reboundFrameIds: string[] = [];
  const patches: FramePatchEntry[] = [];
  const { framesById, frameOrder } = useFramesStore.getState();
  for (const id of frameOrder) {
    const frame = framesById[id];
    if (frame?.frameType !== "conversation") continue;
    if (frame.spaceId !== spaceId) continue;
    const metadata = frame.metadata;
    const frameConversationId = metadataString(
      metadata,
      "conversationId",
      "conversation_id",
    );
    if (frameConversationId !== conversationId) continue;

    const currentExecutionId = metadataString(metadata, "liveExecutionId");
    const currentSessionId = metadataString(metadata, "liveSessionId");
    const currentSequence = metadata.liveBindingSequence;
    const hasCurrentSequence =
      typeof currentSequence === "number" && Number.isFinite(currentSequence);
    const hasIncomingSequence =
      typeof messageSequence === "number" && Number.isFinite(messageSequence);
    if (
      hasCurrentSequence &&
      (!hasIncomingSequence || messageSequence <= currentSequence)
    ) {
      continue;
    }
    const sessionMatches = sessionId
      ? currentSessionId === sessionId
      : currentSessionId === undefined;
    const sequenceNeedsUpdate =
      hasIncomingSequence &&
      (!hasCurrentSequence || messageSequence > currentSequence);
    reboundFrameIds.push(id);
    if (
      currentExecutionId === executionId &&
      sessionMatches &&
      !sequenceNeedsUpdate
    ) {
      continue;
    }

    const nextMetadata: Record<string, unknown> = {
      ...metadata,
      liveExecutionId: executionId,
      ...(hasIncomingSequence ? { liveBindingSequence: messageSequence } : {}),
    };
    if (sessionId) nextMetadata.liveSessionId = sessionId;
    else delete nextMetadata.liveSessionId;
    patches.push([id, { metadata: nextMetadata }]);
  }
  if (patches.length > 0) useFramesStore.getState().patchFrames(patches);
  return reboundFrameIds;
}
